/**
 * Date utility functions
 * Centralizes date handling to avoid UTC timezone issues
 */

/**
 * Get local date string in YYYY-MM-DD format (avoiding UTC timezone issues)
 * @param date - Date object, defaults to current date
 * @returns Local date string in YYYY-MM-DD format
 */
export function getLocalDateString(date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Check if a date string represents today
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns True if the date is today
 */
export function isToday(dateString: string): boolean {
  return dateString === getLocalDateString();
}

/**
 * Get yesterday's date string
 * @returns Yesterday's date in YYYY-MM-DD format
 */
export function getYesterdayString(): string {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return getLocalDateString(yesterday);
}

/**
 * Get tomorrow's date string
 * @returns Tomorrow's date in YYYY-MM-DD format
 */
export function getTomorrowString(): string {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  return getLocalDateString(tomorrow);
}

/**
 * Convert local date (YYYY-MM-DD) to user timezone start of day in ISO format
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns ISO string representing start of day (00:00:00) in user's timezone
 */
export function getLocalDateStartISO(dateString: string): string {
  // Create date object in user's local timezone
  const date = new Date(`${dateString}T00:00:00`);
  return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/**
 * Convert local date (YYYY-MM-DD) to user timezone end of day in ISO format
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns ISO string representing end of day (23:59:59.999) in user's timezone
 */
export function getLocalDateEndISO(dateString: string): string {
  // Create date object in user's local timezone
  const date = new Date(`${dateString}T23:59:59`);
  return date.toISOString().replace(/\.\d{3}Z$/, 'Z');
}

/**
 * Get timezone-aware date range for API queries
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Object with startTime and endTime in ISO format for user's timezone
 */
export function getDateRangeForAPI(dateString: string): { startTime: string; endTime: string } {
  return {
    startTime: getLocalDateStartISO(dateString),
    endTime: getLocalDateEndISO(dateString),
  };
}

/**
 * Format time from date string to 12-hour format (e.g., "2:45 PM")
 * @param dateString - ISO date string
 * @returns Formatted time string or '--' if invalid
 */
export function formatTime(dateString: string): string {
  try {
    const date = new Date(dateString);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, '0');
    return `${displayHours}:${displayMinutes} ${ampm}`;
  } catch {
    return '--';
  }
}

/**
 * Format time from date string to "MMM dd h:mm AM/PM" format (e.g., "Nov 19 3:10 AM")
 * @param dateString - ISO date string
 * @returns Formatted time string with date or '--' if invalid
 */
export function formatTimeWithDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const day = date.getDate();
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    const displayMinutes = minutes.toString().padStart(2, '0');
    return `${month} ${day} ${displayHours}:${displayMinutes} ${ampm}`;
  } catch {
    return '--';
  }
}
